import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, UploadCloud, X } from "lucide-react";
import { SERVICES, BUDGET_RANGES } from "@/data/site";
import { SECTION, BTN_GOLD, INPUT_CLS, API } from "./styles";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const initial = {
  name: "",
  email: "",
  phone: "",
  address: "",
  budget: "",
  start_date: "",
  description: "",
};

export const EstimateForm = () => {
  const [form, setForm] = useState(initial);
  const [services, setServices] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      setServices((prev) => (prev.includes(e.detail) ? prev : [...prev, e.detail]));
    };
    window.addEventListener("apex:preselect-service", handler);
    return () => window.removeEventListener("apex:preselect-service", handler);
  }, []);

  const toggleService = (title) => {
    setServices((prev) =>
      prev.includes(title) ? prev.filter((s) => s !== title) : [...prev, title]
    );
  };

  const onFiles = (e) => {
    const files = Array.from(e.target.files || []).slice(0, 5);
    setPhotos(files);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (services.length === 0) {
      toast.error("Please select at least one service.");
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      services.forEach((s) => fd.append("services", s));
      photos.forEach((p) => fd.append("photos", p));
      await axios.post(`${API}/estimates`, fd);
      toast.success("Estimate request received — we'll be in touch shortly.");
      setForm(initial);
      setServices([]);
      setPhotos([]);
      if (fileRef.current) fileRef.current.value = "";
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please call us instead.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="estimate" data-testid="estimate-section" className={`${SECTION} bg-[#F4F4F1]`}>
      <div className="grid lg:grid-cols-5 gap-14">
        <div className="lg:col-span-2">
          <p className="font-accent-script text-2xl md:text-3xl text-[#D4AF37] mb-3">
            Let's Talk About Your Home
          </p>
          <h2
            className="font-serif text-3xl md:text-5xl font-semibold text-[#0A1F3C] gold-underline"
            data-testid="estimate-headline"
          >
            REQUEST AN ESTIMATE
          </h2>
          <p className="text-[#5A6A85] mt-6 leading-relaxed">
            Tell us about your project and attach photos of the space. You'll receive a confirmation
            right away, and our team will follow up to schedule your free estimate.
          </p>
          <div className="mt-8 border border-[#E2E2DE] bg-white shadow-sm p-6">
            <p className="text-[11px] uppercase tracking-[0.2em] text-[#C9A227] mb-2">
              Prefer to talk?
            </p>
            <p className="text-sm text-[#44506A]">
              Active projects: <a href="tel:+12539219781" className="text-[#0A1F3C] font-medium hover:text-[#C9A227]">(253) 921-9781</a>
            </p>
            <p className="text-sm text-[#44506A] mt-1">
              New projects: <a href="tel:+12066872329" className="text-[#0A1F3C] font-medium hover:text-[#C9A227]">(206) 687-2329</a>
            </p>
          </div>
        </div>

        <form onSubmit={submit} className="lg:col-span-3 space-y-8" data-testid="estimate-form">
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <label className="text-[11px] uppercase tracking-[0.2em] text-[#5A6A85]">Full Name *</label>
              <input required data-testid="estimate-name-input" className={INPUT_CLS} value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="John Smith" />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.2em] text-[#5A6A85]">Email *</label>
              <input required type="email" data-testid="estimate-email-input" className={INPUT_CLS} value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@email.com" />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.2em] text-[#5A6A85]">Phone *</label>
              <input required type="tel" data-testid="estimate-phone-input" className={INPUT_CLS} value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="(804) 555-0123" />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.2em] text-[#5A6A85]">Project Address *</label>
              <input required data-testid="estimate-address-input" className={INPUT_CLS} value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Street, City, VA" />
            </div>
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-[0.2em] text-[#5A6A85] block mb-4">
              Services Needed *
            </label>
            <div className="grid sm:grid-cols-2 gap-3" data-testid="estimate-services-group">
              {SERVICES.map((s) => (
                <button
                  type="button"
                  key={s.id}
                  onClick={() => toggleService(s.title)}
                  data-testid={`estimate-service-${s.id}`}
                  aria-pressed={services.includes(s.title)}
                  className={`text-left text-xs tracking-wide px-4 py-3 border transition-colors duration-300 ${
                    services.includes(s.title)
                      ? "border-[#D4AF37] text-[#C9A227] bg-[#D4AF37]/10"
                      : "border-[#D9D9D4] text-[#44506A] hover:border-[#D4AF37]/60"
                  }`}
                >
                  {s.title}
                </button>
              ))}
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <label className="text-[11px] uppercase tracking-[0.2em] text-[#5A6A85] block mb-2">
                Estimated Budget
              </label>
              <Select value={form.budget} onValueChange={(v) => setForm({ ...form, budget: v })}>
                <SelectTrigger data-testid="estimate-budget-select"
                  className="bg-transparent border-0 border-b border-[#C9C9C4] rounded-none text-[#0A1F3C] px-2 focus:ring-0">
                  <SelectValue placeholder="Select a range" />
                </SelectTrigger>
                <SelectContent className="bg-white border-[#E2E2DE] text-[#0A1F3C]">
                  {BUDGET_RANGES.map((b) => (
                    <SelectItem key={b} value={b} data-testid={`estimate-budget-${b.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
                      {b}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.2em] text-[#5A6A85] block mb-2">
                Preferred Start Date
              </label>
              <input type="date" data-testid="estimate-startdate-input" className={INPUT_CLS}
                value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
            </div>
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-[0.2em] text-[#5A6A85]">Project Details</label>
            <textarea rows={4} data-testid="estimate-description-input" className={`${INPUT_CLS} resize-none`}
              value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Tell us about your project — rooms, sizes, goals, timeline..." />
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-[0.2em] text-[#5A6A85] block mb-3">
              Project Photos (up to 5)
            </label>
            <label
              data-testid="estimate-photos-dropzone"
              className="flex items-center justify-center gap-3 border border-dashed border-[#C9C9C4] hover:border-[#D4AF37] transition-colors duration-300 px-6 py-8 cursor-pointer text-[#5A6A85] text-sm bg-white"
            >
              <UploadCloud className="h-5 w-5 text-[#D4AF37]" />
              {photos.length ? `${photos.length} photo(s) selected` : "Click to upload photos of your space"}
              <input ref={fileRef} type="file" accept="image/*" multiple className="hidden"
                data-testid="estimate-photos-input" onChange={onFiles} />
            </label>
            {photos.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {photos.map((p, i) => (
                  <span key={i} className="flex items-center gap-2 text-xs text-[#44506A] border border-[#D9D9D4] bg-white px-3 py-1.5">
                    {p.name}
                    <button type="button" aria-label="Remove photo"
                      onClick={() => setPhotos(photos.filter((_, j) => j !== i))}>
                      <X className="h-3 w-3 text-[#D4AF37]" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <button type="submit" disabled={loading} data-testid="estimate-submit-btn"
            className={`${BTN_GOLD} w-full md:w-auto disabled:opacity-60`}>
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            {loading ? "Sending..." : "Submit Estimate Request"}
          </button>
        </form>
      </div>
    </section>
  );
};
